import React from 'react';
import { Box, Divider, Typography, Checkbox, FormControlLabel } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { TimerOutlined, GridViewOutlined, ViewStreamOutlined } from '@mui/icons-material';
import { COUNTDOWN_COLOR } from '../constants';
import { InspectorSection, StyleRow, PanelHeader, ColorInput, PxInput } from '../ui/primitives';

// ─── CountdownInspector ───────────────────────────────────────────────────────
export function CountdownInspector({ el, onUpdate, onClose }) {
  const update = (key, val) => onUpdate({ ...el, [key]: val });

  const show = el.show || { days: true, hours: true, mins: true, secs: true };

  const LayoutBtn = ({ value, label, icon }) => {
    const active = (el.layout || '2x2') === value;
    return (
      <Box
        onClick={() => update('layout', value)}
        sx={{
          flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
          py: 1, borderRadius: 1.5, cursor: 'pointer', border: `1.5px solid ${active ? COUNTDOWN_COLOR : '#e4e9f0'}`,
          background: active ? alpha(COUNTDOWN_COLOR, 0.07) : '#f8f9fb',
          color: active ? COUNTDOWN_COLOR : '#7a8494',
          transition: 'all 0.15s',
          '&:hover': { borderColor: COUNTDOWN_COLOR, color: COUNTDOWN_COLOR },
        }}
      >
        {icon}
        <Typography sx={{ fontSize: '0.67rem', fontWeight: 600 }}>{label}</Typography>
      </Box>
    );
  };

  const UnitCheck = ({ unitKey, label }) => (
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          checked={!!show[unitKey]}
          onChange={e => update('show', { ...show, [unitKey]: e.target.checked })}
          sx={{
            color: '#c0c8d4',
            '&.Mui-checked': { color: COUNTDOWN_COLOR },
            p: '4px',
          }}
        />
      }
      label={
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#1a1a2e' }}>{label}</Typography>
      }
      sx={{ ml: 0, mr: 0, gap: 0.25 }}
    />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader icon={<TimerOutlined sx={{ fontSize: 14 }} />} label="Countdown" color={COUNTDOWN_COLOR} onClose={onClose} />
      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', overflowX: 'hidden' }}>

        {/* ── Target Date ── */}
        <InspectorSection label="Timer">
          <StyleRow label="Date">
            <input
              type="datetime-local"
              value={el.targetDate || ''}
              onChange={e => update('targetDate', e.target.value)}
              style={{
                width: '100%', border: '1px solid #e4e9f0', borderRadius: 6,
                padding: '4px 8px', fontSize: '12px', outline: 'none',
                fontFamily: 'inherit', background: '#f8f9fb', boxSizing: 'border-box',
                cursor: 'pointer',
              }}
            />
          </StyleRow>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        {/* ── Layout ── */}
        <InspectorSection label="Layout">
          <Box sx={{ display: 'flex', gap: 1 }}>
            <LayoutBtn value="2x2" label="2×2 Grid" icon={<GridViewOutlined sx={{ fontSize: 18 }} />} />
            <LayoutBtn value="4x1" label="4×1 Row"  icon={<ViewStreamOutlined sx={{ fontSize: 18 }} />} />
          </Box>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        {/* ── Display units ── */}
        <InspectorSection label="Display Units">
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
            <UnitCheck unitKey="days"  label="Days"    />
            <UnitCheck unitKey="hours" label="Hours"   />
            <UnitCheck unitKey="mins"  label="Minutes" />
            <UnitCheck unitKey="secs"  label="Seconds" />
          </Box>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        {/* ── Typography ── */}
        <InspectorSection label="Typography">
          <StyleRow label="Number">
            <PxInput value={el.numberSize} onChange={v => update('numberSize', v)} placeholder="48" />
          </StyleRow>
          <StyleRow label="Label">
            <PxInput value={el.labelSize} onChange={v => update('labelSize', v)} placeholder="13" />
          </StyleRow>
          <StyleRow label="Num color">
            <ColorInput value={el.numberColor || '#1a1a2e'} onChange={v => update('numberColor', v)} />
          </StyleRow>
          <StyleRow label="Lbl color">
            <ColorInput value={el.labelColor || '#7a8494'} onChange={v => update('labelColor', v)} />
          </StyleRow>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        {/* ── Card appearance ── */}
        <InspectorSection label="Card">
          <StyleRow label="Background">
            <ColorInput value={el.cardBackground || '#f1f5f9'} onChange={v => update('cardBackground', v)} />
          </StyleRow>
          <StyleRow label="Radius">
            <PxInput value={el.cardRadius} onChange={v => update('cardRadius', v)} placeholder="12" />
          </StyleRow>
          <StyleRow label="Height">
            <PxInput value={el.cardHeight} onChange={v => update('cardHeight', v)} placeholder="100" />
          </StyleRow>
        </InspectorSection>

      </Box>
    </Box>
  );
}
